import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Monitor, Check, Loader2 } from "lucide-react";
import { useApi } from "@/hooks/useApi";
import { useToast } from "@/hooks/useToast";
import { isTvExportable, mergeForExport } from "@/utils/featureParams";

const UNSUPPORTED_COUNT_NOTICE =
  "The TradingView bridge currently supports 5 feature slots; this study uses a different count.";

interface ExportForTvButtonProps {
  bestParams: Record<string, unknown>;
  fixedValues: Record<string, unknown>; // flat fixed/pinned values from backend results API
  disabled?: boolean;
}

export function ExportForTvButton({
  bestParams,
  fixedValues,
  disabled,
}: ExportForTvButtonProps) {
  const api = useApi();
  const { toast } = useToast();
  const [status, setStatus] = useState<"idle" | "loading" | "success">("idle");
  const [defaults, setDefaults] = useState<Record<string, unknown> | null>(
    null,
  );

  // AIE-534: the LC indicator bridge serializes exactly 5 slots; block export for any other arity.
  const exportable = isTvExportable(fixedValues);

  const handleExport = async () => {
    setStatus("loading");
    try {
      // Fetch canonical defaults if not cached
      let lcDefaults = defaults;
      if (!lcDefaults) {
        lcDefaults = await api.tvBridgeGetDefaults();
        setDefaults(lcDefaults);
      }

      // Merge: defaults <- backend fixed/pinned values (incl. the chosen fN_type) <- best_params
      // (optimized fN_period wins), filtered to LC keys (best_params carries non-LC keys like
      // minimum_trades). Shared helper so the chosen type + optimized period export coherently.
      const filtered = mergeForExport(lcDefaults, fixedValues, bestParams);

      await api.tvBridgeStageSettings(filtered);
      setStatus("success");
      toast({
        title: "Settings staged",
        description:
          "Open the LC indicator settings and click 'Import from Optimizer'.",
      });
      setTimeout(() => setStatus("idle"), 10000);
    } catch {
      setStatus("idle");
      toast({
        title: "Staging failed",
        description: "Could not stage settings. Please try again.",
        variant: "destructive",
      });
    }
  };

  return (
    <div className="flex flex-col gap-1">
      <Button
        variant="outline"
        size="sm"
        onClick={handleExport}
        disabled={disabled || status === "loading" || !exportable}
        title={exportable ? undefined : UNSUPPORTED_COUNT_NOTICE}
      >
        {status === "loading" && (
          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
        )}
        {status === "success" && <Check className="h-4 w-4 mr-2" />}
        {status === "idle" && <Monitor className="h-4 w-4 mr-2" />}
        {status === "success"
          ? "Settings Staged"
          : "Stage Settings for LC Indicator"}
      </Button>
      {!exportable && (
        <p className="text-xs text-muted-foreground">
          {UNSUPPORTED_COUNT_NOTICE}
        </p>
      )}
    </div>
  );
}
